import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AccountingShell from "./AccountingShell";

const INR = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

function DashboardContent() {
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [apps, setApps] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [tenders, setTenders] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [vSnap, aSnap, rSnap, tSnap] = await Promise.all([
        getDocs(collection(db, "vendors")),
        getDocs(collection(db, "tender_applications")),
        getDocs(collection(db, "ra_bills")),
        getDocs(collection(db, "tenders")),
      ]);

      setVendors(vSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setApps(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setRaBills(rSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTenders(tSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    return {
      vendors: vendors.length,
      pendingVendors: vendors.filter(
        (v) => (v.registrationFeeStatus || v.status || "pending") !== "approved"
      ).length,
      tenderApps: apps.length,
      emdHeld: apps.reduce(
        (s, a) => s + ((a.emdStatus === "paid" ? Number(a.emdAmount || 0) : 0)),
        0
      ),
      raBills: raBills.length,
      raAmount: raBills.reduce(
        (s, r) => s + Number(r.totalAmount || r.grossAmount || 0),
        0
      ),
      tenderValue: tenders.reduce(
        (s, t) => s + Number(t.awardedAmount || t.baseAmount || t.amount || 0),
        0
      ),
    };
  }, [vendors, apps, raBills, tenders]);

  if (loading) {
    return <div className="bg-white rounded-2xl p-8">Loading dashboard...</div>;
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <Card title="Total Vendors" value={stats.vendors} />
        <Card title="Pending Registration" value={stats.pendingVendors} />
        <Card title="Tender Applications" value={stats.tenderApps} />
        <Card title="Total EMD Held" value={INR(stats.emdHeld)} />
        <Card title="RA Bills" value={stats.raBills} />
        <Card title="RA Bill Amount" value={INR(stats.raAmount)} />
        <Card title="Tender Value" value={INR(stats.tenderValue)} />
        <Card title="System Status" value="LIVE" />
      </div>

      <div className="mt-8 bg-white border rounded-2xl p-6">
        <h2 className="font-black text-lg mb-4">Accounting Flow</h2>
        <div className="grid md:grid-cols-5 gap-4">
          {[
            "Receipt Upload",
            "Accountant Verify",
            "CEO Approval",
            "Invoice + Ledger",
            "Payment / Refund",
          ].map((x, i) => (
            <div key={x} className="border rounded-2xl p-5 text-center bg-slate-50">
              <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">
                {i + 1}
              </div>
              <p className="font-bold text-sm">{x}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Card({ title, value }) {
  return (
    <div className="bg-white border rounded-2xl p-5 shadow-sm">
      <p className="text-xs text-slate-500">{title}</p>
      <h2 className="text-xl font-black mt-2 text-slate-900">{value}</h2>
    </div>
  );
}

export default function AccountingDashboard() {
  return (
    <AccountingShell title="Accounting Dashboard">
      <DashboardContent />
    </AccountingShell>
  );
}